<script setup lang="ts">
import { ref, watch, computed, inject, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import {
  postCareOrderDetail,
  buildCareOrderChecklistRowsFromDetailItems,
  careOrderChecklistSummaryTokensFromDetail,
  postCareOrderCreateFollowUp,
  canCreateFollowUpFromChecklistRow,
  issueLevelFromChecklistStatusTone,
  buildFollowUpNotesFromChecklistRow,
  bookedTimeFromCareOrderRow,
  bookedTimeFromFollowUpRow,
  isBookedScheduleDisplayLabel,
  preferredTimesFromCareOrderRow,
  resolveCareOrderDisplayUserId,
  normalizeCareOrderPayments,
  careOrderPaymentIsSuccess,
  careOrderPaymentShowsAttempt,
  careOrderPaymentAttemptLabel,
  careOrderPaymentTransactionId,
  type CareOrderListItem,
  type CareOrderListFollowup,
  type CareOrderPayment,
  type CareOrderTimelineEvent,
  type CareOrderChecklistDisplayRow,
  type CareOrderChecklistStatusTone,
  type OrderCardDTO,
} from '@/api/orders'
import {
  CARE_SERVICE_STATUS_COMPLETED,
  CARE_SERVICE_STATUS_FU_ASSIGNED,
  CARE_SERVICE_STATUS_FU_COMPLETED,
  classifyCareSidebarSubBucket,
} from '@/api/hmCareOrderTypes'
import {
  formatOrderScheduleLineDisplay,
  isPendingAssignmentLabel,
  orderStatusIsFollowUpContext,
  PENDING_RESCHEDULE_TIME_LABEL,
  PENDING_SCHEDULE_TIME_LABEL,
  productCodeTagToneClass,
} from '@/api/careOrderLineDisplay'
import { formatServiceTimeRangeLine } from '@/utils/orderScheduleDisplay'
import AssignNameTipHost from './AssignNameTipHost.vue'
import imgClose from '@/assets/Fill 1@2x.png'
import imgAlert from '@/assets/alert@2x.png'
import imgNormal from '@/assets/normal@2x.png'
import imgErrorRed from '@/assets/error.red@2x.png'
import imgUnable from '@/assets/error@2x.png'
import imgPending from '@/assets/pending@2x.png'
import imgRightArrow from '@/assets/right arrow@2x.png'
import imgMessage from '@/assets/Message@2x.png'
import imgNote from '@/assets/note@2x.png'
import imgSchedule from '@/assets/Schedule,black@2x.png'
import imgRescheduleGlyph from '@/assets/reschedule@2x.png'
import imgRefund from '@/assets/refund@2x.png'
import CareOrderChecklistItemDetailView from './CareOrderChecklistItemDetailView.vue'
import LoadingIndicator from '@/components/LoadingIndicator.vue'
import { writeUsersListPrefill, resolveUserDetailPk } from '@/api/userslist'
import { usersDetailRoute } from '@/utils/libraryRoutes'
import { normalizePhoneSearchDigits } from '@/utils/technicianListDisplay'

const props = defineProps<{
  order: OrderCardDTO | null
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'ordersRefresh'): void
  (e: 'add-note', order: OrderCardDTO): void
  (e: 'schedule', order: OrderCardDTO): void
  (e: 'reschedule', order: OrderCardDTO): void
  (e: 'refund', order: OrderCardDTO): void
}>()

const router = useRouter()
const showToast = inject<(type: 'success' | 'warning' | 'error', message: string) => void>(
  'showToast',
)

const activeTab = ref<'information' | 'checklist'>('information')
const loading = ref(false)
const followUpSubmitting = ref(false)
const selectedChecklistRow = ref<CareOrderChecklistDisplayRow | null>(null)
const detail = ref<CareOrderListItem | null>(null)
const loadError = ref('')

const detailFollowUp = computed((): CareOrderListFollowup | null => detail.value?.followups?.[0] ?? null)

const detailChecklistState = computed(() => {
  const d = detail.value
  if (!d) {
    return { summaryTokens: [] as string[], checklistRows: [] as CareOrderChecklistDisplayRow[] }
  }
  return {
    summaryTokens: careOrderChecklistSummaryTokensFromDetail(d),
    checklistRows: buildCareOrderChecklistRowsFromDetailItems(d?.items, d),
  }
})

const summaryTokens = computed(() => detailChecklistState.value.summaryTokens)

const checklistRows = computed(() => detailChecklistState.value.checklistRows)

const TIMELINE_MONTH = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

function pickDetailStringField(...keys: string[]): string {
  const row = detail.value
  if (!row) return ''
  const rec = row as unknown as Record<string, unknown>
  for (const key of keys) {
    const v = rec[key]
    if (typeof v === 'string' && v.trim()) return v.trim()
  }
  return ''
}

function timelineWhenInput(raw: string): string {
  const s = raw.trim()
  if (!s) return s
  const dashIdx = s.indexOf(' - ')
  if (dashIdx > 0) {
    const tail = s.slice(dashIdx + 3).trim()
    if (/\d{1,2}:\d{2}\s*(AM|PM)/i.test(tail)) {
      return s.slice(0, dashIdx).trim()
    }
  }
  return s
}

function formatTimelineWhen(raw: string): string {
  const candidate = timelineWhenInput(raw)
  if (!candidate) return raw.trim()
  const parsed = Date.parse(candidate.replace(/\./g, '/'))
  if (Number.isNaN(parsed)) {
    if (/^\w{3}\s+\d{1,2},\s+\d{4},/i.test(candidate)) return candidate
    return candidate
  }
  const d = new Date(parsed)
  const mon = TIMELINE_MONTH[d.getMonth()]
  const day = String(d.getDate()).padStart(2, '0')
  const year = d.getFullYear()
  let h = d.getHours()
  const ampm = h >= 12 ? 'PM' : 'AM'
  h = h % 12 || 12
  const min = String(d.getMinutes()).padStart(2, '0')
  return `${mon} ${day}, ${year}, ${String(h).padStart(2, '0')}:${min}${ampm}`
}

interface InfoTimelineEvent {
  at: string
  label: string
  actor?: string
}

function detailServiceStatusCode(): string {
  return (detail.value?.service_status ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_')
}

function detailServiceStatusLabel(): string {
  return (
    detail.value?.service_status_text?.trim() ||
    detail.value?.service_status?.trim() ||
    ''
  )
}

function detailIsFollowUpStatus(): boolean {
  const code = detailServiceStatusCode()
  if (code.startsWith('fu_') || code.includes('follow_up')) return true
  return orderStatusIsFollowUpContext(detailServiceStatusLabel())
}

function readDetailActionFlag(raw: unknown): boolean {
  return raw === 1 || raw === true || raw === '1'
}

function detailOrderAction(key: string): boolean {
  const actions = detail.value?.available_actions
  if (!actions || typeof actions !== 'object') return false
  const order = actions.order
  if (!order || typeof order !== 'object') return false
  return readDetailActionFlag(order[key])
}

function detailFollowUpAction(key: string): boolean {
  const actions = detail.value?.available_actions
  if (!actions || typeof actions !== 'object') return false
  const list = actions.followups
  if (Array.isArray(list) && list[0] && typeof list[0] === 'object') {
    return readDetailActionFlag(list[0][key])
  }
  if (actions.followup && typeof actions.followup === 'object') {
    return readDetailActionFlag(actions.followup[key])
  }
  return false
}

function detailTechnicianName(): string {
  const tech = detail.value?.technician
  if (tech && typeof tech === 'object') {
    const name = String(tech.name ?? tech.full_name ?? '').trim()
    if (name) return name
  }
  return (detail.value?.assignment_text ?? '').trim()
}

function detailTechnicianStaffNoRef(): string {
  const tech = detail.value?.technician
  if (tech && typeof tech === 'object') {
    return String(tech.staff_no ?? tech.staffNo ?? '').trim()
  }
  return ''
}

function detailAssigneeRoleLabel(): string {
  const tech = detail.value?.technician
  if (tech && typeof tech === 'object') {
    const roleText = String(tech.role_text ?? '').trim()
    if (roleText) return roleText
  }
  const assigneeRole = detail.value?.assignee_role_text?.trim()
  if (assigneeRole) return assigneeRole
  return 'Inspector'
}

function detailFollowUpTechnicianName(fu: CareOrderListFollowup): string {
  const tech = fu.technician
  if (tech && typeof tech === 'object') {
    const name = String(tech.name ?? tech.full_name ?? '').trim()
    if (name) return name
  }
  return (fu.assignment_text ?? '').trim()
}

const isInfoMainPendingReschedule = computed(() => {
  if (!detail.value || detailIsFollowUpStatus()) return false
  const code = detailServiceStatusCode()
  if (code === 'pending_reschedule') return true
  const status = detailServiceStatusLabel()
  if (status === 'Pending Reschedule') return true
  return classifyCareSidebarSubBucket(status) === 'pend_resched'
})

const isInfoMainAssigned = computed(() => {
  if (!detail.value || detailIsFollowUpStatus()) return false
  const code = detailServiceStatusCode()
  if (code === 'assigned') return true
  const status = detailServiceStatusLabel()
  if (status === 'Assigned') return true
  const s = status.toLowerCase()
  if (!s || s.includes('pending') || s.includes('reschedule') || s.includes('complete')) return false
  if (s.includes('unassigned')) return false
  return s === 'assigned'
})

const isInfoMainCompleted = computed(() => {
  if (!detail.value || detailIsFollowUpStatus()) return false
  const code = detailServiceStatusCode()
  if (code === 'completed') return true
  const status = detailServiceStatusLabel()
  if (status === CARE_SERVICE_STATUS_COMPLETED) return true
  const s = status.toLowerCase()
  if (!s || s.includes('incomplete') || s.includes('pending') || s.includes('reschedule')) return false
  return /\bcompleted\b/.test(s) || /\bcomplete\b/.test(s)
})

const isInfoFuPendingSchedule = computed(() => {
  if (!detail.value) return false
  const code = detailServiceStatusCode()
  if (code === 'fu_pending_schedule' || code === 'follow_up_pending_schedule') return true
  return classifyCareSidebarSubBucket(detailServiceStatusLabel()) === 'fu_pend_sched'
})

const isInfoFuPendingReschedule = computed(() => {
  if (!detail.value) return false
  const code = detailServiceStatusCode()
  if (code === 'fu_pending_reschedule' || code === 'follow_up_pending_reschedule') return true
  return classifyCareSidebarSubBucket(detailServiceStatusLabel()) === 'fu_pend_resched'
})

const isInfoFuContext = computed(
  () => isInfoFuPendingSchedule.value || isInfoFuPendingReschedule.value,
)

const isInfoFuAssigned = computed(() => {
  if (!detail.value) return false
  if (isInfoFuPendingSchedule.value || isInfoFuPendingReschedule.value) return false
  const code = detailServiceStatusCode()
  if (code === 'fu_completed' || code === 'follow_up_completed') return false
  if (code === 'fu_assigned' || code === 'follow_up_assigned') return true
  const status = detailServiceStatusLabel()
  if (status === CARE_SERVICE_STATUS_FU_ASSIGNED) return true
  if (orderStatusIsFollowUpContext(status)) {
    const n = status.toLowerCase()
    if (n.includes('completed') && !n.includes('incomplete')) return false
    if (n.includes('assigned') && !n.includes('pending')) return true
  }
  return false
})

const isInfoFuCompleted = computed(() => {
  if (!detail.value) return false
  const code = detailServiceStatusCode()
  if (code === 'fu_completed' || code === 'follow_up_completed') return true
  const status = detailServiceStatusLabel()
  if (status === CARE_SERVICE_STATUS_FU_COMPLETED) return true
  const n = status.toLowerCase()
  return /follow[\s-]*up/.test(n) && /\bcompleted\b/.test(n) && !n.includes('incomplete')
})

/** FU 跟进行区块：Pending Schedule / Reschedule / Assigned */
const isInfoFuFollowUpBlock = computed(
  () => isInfoFuContext.value || isInfoFuAssigned.value,
)

const infoPreferredTimeTipEntries = computed((): string[] => {
  const row = detail.value
  if (!row) return []
  return preferredTimesFromCareOrderRow(row).map((pt) => formatOrderScheduleLineDisplay(pt))
})

const infoServiceTimeTipVisible = computed(() => {
  if (!isInfoMainPendingReschedule.value) return false
  return infoPreferredTimeTipEntries.value.length > 0
})

const infoFollowUpPreferredTimeTipVisible = computed(() => {
  if (!isInfoFuPendingReschedule.value) return false
  return infoPreferredTimeTipEntries.value.length > 0
})

const serviceTimeTipHostRef = ref<HTMLElement | null>(null)
const followUpTimeTipHostRef = ref<HTMLElement | null>(null)
const serviceTimeTipVisible = ref(false)
const followUpTimeTipVisible = ref(false)
const serviceTimeTipStyle = ref<{ top: string; left: string }>({ top: '0px', left: '0px' })
const followUpTimeTipStyle = ref<{ top: string; left: string }>({ top: '0px', left: '0px' })

function updateServiceTimeTipPosition(): void {
  const el = serviceTimeTipHostRef.value
  if (!el) return
  const r = el.getBoundingClientRect()
  serviceTimeTipStyle.value = {
    top: `${r.bottom + 8}px`,
    left: `${r.left}px`,
  }
}

function updateFollowUpTimeTipPosition(): void {
  const el = followUpTimeTipHostRef.value
  if (!el) return
  const r = el.getBoundingClientRect()
  followUpTimeTipStyle.value = {
    top: `${r.bottom + 8}px`,
    left: `${r.left}px`,
  }
}

function showServiceTimeTip(): void {
  updateServiceTimeTipPosition()
  serviceTimeTipVisible.value = true
}

function hideServiceTimeTip(): void {
  serviceTimeTipVisible.value = false
}

function showFollowUpTimeTip(): void {
  updateFollowUpTimeTipPosition()
  followUpTimeTipVisible.value = true
}

function hideFollowUpTimeTip(): void {
  followUpTimeTipVisible.value = false
}

const TIMELINE_EVENT_TYPE_LABELS: Record<string, string> = {
  order_placed: 'Order placed',
  paid: 'Paid',
  scheduled: 'HM Care scheduled',
  reschedule_requested: 'Reschedule requested',
  technician_status_updated: 'Technician status updated',
}

function pickTimelineRecordString(rec: Record<string, unknown>, keys: string[]): string {
  for (const key of keys) {
    const v = rec[key]
    if (typeof v === 'string' && v.trim()) return v.trim()
  }
  return ''
}

function formatTimelineEventTypeLabel(eventType: string): string {
  const key = eventType.trim().toLowerCase()
  if (TIMELINE_EVENT_TYPE_LABELS[key]) return TIMELINE_EVENT_TYPE_LABELS[key]
  return eventType
    .trim()
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

function resolveTimelineEventLabel(ev: CareOrderTimelineEvent): string {
  const title = typeof ev.title === 'string' ? ev.title.trim() : ''
  if (title) return title
  const eventType = typeof ev.event_type === 'string' ? ev.event_type.trim() : ''
  if (eventType) return formatTimelineEventTypeLabel(eventType)
  return ''
}

function resolveTimelineEventAt(ev: CareOrderTimelineEvent): string {
  const rec = ev as Record<string, unknown>
  return pickTimelineRecordString(rec, [
    'occurred_at',
    'event_at',
    'event_time',
    'created_at',
    'create_time',
    'at',
    'happened_at',
    'timestamp',
    'time',
  ])
}

function resolveTimelineEventActor(ev: CareOrderTimelineEvent): string | undefined {
  const rec = ev as Record<string, unknown>
  const direct = pickTimelineRecordString(rec, [
    'actor_name',
    'actor_text',
    'by_name',
    'by',
    'operator_name',
    'created_by_name',
    'created_by',
    'create_by',
    'user_name',
  ])
  if (direct) return direct
  for (const key of ['actor', 'operator', 'created_by', 'user'] as const) {
    const value = rec[key]
    if (typeof value === 'string' && value.trim()) return value.trim()
    if (value && typeof value === 'object') {
      const name = String(
        (value as Record<string, unknown>).name ??
          (value as Record<string, unknown>).full_name ??
          '',
      ).trim()
      if (name) return name
    }
  }
  return undefined
}

function mapCareOrderTimelineEvent(ev: CareOrderTimelineEvent): InfoTimelineEvent | null {
  const label = resolveTimelineEventLabel(ev)
  const atRaw = resolveTimelineEventAt(ev)
  if (!label || !atRaw) return null
  const actor = resolveTimelineEventActor(ev)
  return {
    at: formatTimelineWhen(atRaw),
    label,
    ...(actor ? { actor } : {}),
  }
}

function infoDisplay(v: string | undefined | null): string {
  const s = (v ?? '').trim()
  return s && s !== '—' ? s : 'N/A'
}

function formatInfoMoney(amount: number | undefined | null): string {
  if (amount == null || !Number.isFinite(amount)) return '$0.00'
  return `$${amount.toFixed(2)}`
}

function looksLikeCouponCode(value: string): boolean {
  const text = value.trim()
  if (!text) return true
  if (/^n\/?a$/i.test(text) || text === '—' || text === '-') return true
  if (/[$%]/.test(text) || /^-?\d+(\.\d+)?$/.test(text)) return false
  return /^[A-Z0-9_]{8,}$/i.test(text)
}

function formatCouponDiscountAmount(amount: number): string {
  if (!Number.isFinite(amount) || amount === 0) return ''
  const abs = Math.abs(amount).toFixed(2)
  return amount < 0 ? `-$${abs}` : `$${abs}`
}

function coerceCouponDiscount(value: unknown): string {
  if (value == null || value === '') return ''
  if (typeof value === 'number') return formatCouponDiscountAmount(value)
  if (typeof value === 'string') {
    const text = value.trim()
    if (!text || looksLikeCouponCode(text)) return ''
    if (text.startsWith('$') || text.startsWith('-$')) return text
    const parsed = Number(text.replace(/[$,\s]/g, ''))
    if (Number.isFinite(parsed)) return formatCouponDiscountAmount(parsed)
    return ''
  }
  if (typeof value === 'object') {
    const rec = value as Record<string, unknown>
    return (
      coerceCouponDiscount(rec.discount_amount_text) ||
      coerceCouponDiscount(rec.coupon_amount_text) ||
      coerceCouponDiscount(rec.coupon_discount_text) ||
      coerceCouponDiscount(rec.discount_text) ||
      coerceCouponDiscount(rec.discount_amount) ||
      coerceCouponDiscount(rec.coupon_amount) ||
      coerceCouponDiscount(rec.coupon_discount) ||
      coerceCouponDiscount(rec.amount) ||
      coerceCouponDiscount(rec.value)
    )
  }
  return ''
}

function couponSourceRecords(row: Record<string, unknown>): Record<string, unknown>[] {
  const bags = [row]
  for (const key of ['detail', 'order_detail', 'payment', 'finance', 'pricing', 'order'] as const) {
    const nested = row[key]
    if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
      bags.push(nested as Record<string, unknown>)
    }
  }
  return bags
}

const COUPON_DISCOUNT_KEYS = [
  'coupon_discount_text',
  'coupon_amount_text',
  'discount_amount_text',
  'coupon_text',
  'discount_text',
  'coupon_discount',
  'coupon_amount',
  'discount_amount',
  'discount',
  'coupon',
] as const

function pickCouponDiscountFromRecord(rec: Record<string, unknown>): string {
  for (const key of COUPON_DISCOUNT_KEYS) {
    const display = coerceCouponDiscount(rec[key])
    if (display) return display
  }
  return ''
}

function pickDetailCouponDiscount(): string {
  const row = detail.value
  if (!row) return ''
  const rec = row as unknown as Record<string, unknown>
  for (const bag of couponSourceRecords(rec)) {
    const display = pickCouponDiscountFromRecord(bag)
    if (display) return display
  }
  return ''
}

function planKeyToLabel(planKey: string): string {
  return planKey
    .trim()
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

const infoSubscriptionId = computed(() => infoDisplay(detail.value?.subscription_id))

const infoOrderId = computed(() => infoDisplay(detail.value?.order_id))

const infoUserId = computed(() => infoDisplay(resolveCareOrderDisplayUserId(detail.value ?? undefined)))

const infoServiceStatus = computed(() =>
  infoDisplay(detail.value?.service_status_text || detail.value?.service_status),
)

const infoCarePlan = computed(() => {
  const fromDetail = pickDetailStringField('service_name', 'plan_label', 'care_plan')
  if (fromDetail) return fromDetail
  const planKey = detail.value?.plan_key?.trim()
  if (planKey) return planKeyToLabel(planKey)
  const ct = detail.value?.care_template
  if (ct?.plan_key?.trim()) return planKeyToLabel(ct.plan_key)
  return 'N/A'
})

const infoServiceCycle = computed(() => {
  const fromDetail = pickDetailStringField(
    'service_cycle_text',
    'service_cycle',
    'subscription_cycle',
    'cycle_display',
  )
  if (fromDetail) return fromDetail

  const ct = detail.value?.care_template
  if (ct && typeof ct === 'object') {
    for (const key of ['cycle_text', 'service_cycle_text', 'name', 'template_name'] as const) {
      const v = ct[key]
      if (typeof v === 'string' && v.trim()) return v.trim()
    }
    if (ct.template_key?.trim()) return planKeyToLabel(ct.template_key)
  }

  const cycleEnd = pickDetailStringField(
    'service_cycle_end',
    'cycle_end',
    'subscription_end',
    'expire_at',
  )
  const placedRaw = detail.value?.placed_at?.trim() || detail.value?.create_time?.trim() || ''
  if (placedRaw && cycleEnd) {
    const start = formatOrderScheduleLineDisplay(placedRaw)
    const end = formatOrderScheduleLineDisplay(cycleEnd)
    if (start !== '—' && end !== '—') return `${start} - ${end}`
  }

  return 'N/A'
})

const infoServiceAddress = computed(() => infoDisplay(detail.value?.service_address))

const infoRegisteredMobileRaw = computed(() => {
  const fromDetail = pickDetailStringField(
    'registered_mobile',
    'registered_phone',
    'user_mobile',
    'user_phone',
  )
  if (fromDetail) return fromDetail
  return detail.value?.contact_phone?.trim() || ''
})

const infoRegisteredMobile = computed(() => infoDisplay(infoRegisteredMobileRaw.value))

const hasRegisteredMobile = computed(() => !!infoRegisteredMobileRaw.value)

const infoContactPerson = computed(() => infoDisplay(detail.value?.contact_name))

const infoPhoneNumber = computed(() => infoDisplay(detail.value?.contact_phone))

const infoCheckSummaryTokens = computed(() =>
  summaryTokens.value.filter((t) => !/^B-\d+$/i.test(t.trim())),
)

const infoServiceTime = computed(() => {
  const d = detail.value
  if (!d) return 'N/A'

  if (isInfoMainPendingReschedule.value) {
    return infoPreferredTimeTipEntries.value.length
      ? PENDING_SCHEDULE_TIME_LABEL
      : PENDING_RESCHEDULE_TIME_LABEL
  }

  const timeText = d.service_time_text?.trim()
  if (timeText) {
    if (/\bpending\s+(schedule|reschedule)\b/i.test(timeText)) return timeText
    const formatted = formatOrderScheduleLineDisplay(timeText)
    return formatted !== '—' ? formatted : timeText
  }

  const startRaw = d.service_time_start?.trim()
  const endRaw = d.service_time_end?.trim()
  const dateYmd = d.service_date?.trim()
  if (startRaw && endRaw) {
    const range = formatServiceTimeRangeLine(startRaw, endRaw, dateYmd)
    const formatted = formatOrderScheduleLineDisplay(range)
    if (formatted !== '—') return formatted
  }

  const booked = bookedTimeFromCareOrderRow(d)
  if (booked && isBookedScheduleDisplayLabel(booked)) {
    const formatted = formatOrderScheduleLineDisplay(booked)
    return formatted !== '—' ? formatted : booked
  }

  const code = detailServiceStatusCode()
  if (code === 'pending_schedule' || /\bpending\s+schedule\b/i.test(detailServiceStatusLabel())) {
    return 'Pending Schedule'
  }
  return 'N/A'
})

const infoServiceTimeMuted = computed(() =>
  /\bpending\s+(schedule|reschedule)\b/i.test(infoServiceTime.value),
)

const infoAssigneeRoleLabel = computed(() => detailAssigneeRoleLabel())

const infoInspector = computed(() => {
  const name = detailTechnicianName()
  if (!name) return 'Pending Assignment'
  if (isPendingAssignmentLabel(name)) return name
  return name
})

const infoInspectorShowsAssigned = computed(() => {
  const name = detailTechnicianName()
  if (!name || isPendingAssignmentLabel(name)) return false
  return (
    isInfoMainAssigned.value ||
    isInfoMainCompleted.value ||
    isInfoFuFollowUpBlock.value ||
    isInfoFuCompleted.value ||
    !!detailTechnicianStaffNoRef()
  )
})

const infoInspectorName = computed(() => infoDisplay(detailTechnicianName()))

const infoInspectorRefDisplay = computed(() => detailTechnicianStaffNoRef())

const infoInspectorRefTextRef = ref<HTMLElement | null>(null)
const infoInspectorRefIsOverflowing = ref(false)

const infoInspectorRefTipText = computed(() =>
  infoInspectorRefIsOverflowing.value ? infoInspectorRefDisplay.value : '',
)

function syncInspectorRefOverflow(): void {
  const textEl = infoInspectorRefTextRef.value
  const refEl = textEl?.closest('.cod-info-inspector-ref') as HTMLElement | null
  if (!textEl || !refEl) {
    infoInspectorRefIsOverflowing.value = false
    return
  }
  const iconEl = refEl.querySelector('.cod-info-inspector-ref-ic')
  const gap = 6
  const iconWidth = iconEl ? iconEl.getBoundingClientRect().width + gap : 0
  const available = refEl.clientWidth - iconWidth
  infoInspectorRefIsOverflowing.value =
    textEl.scrollWidth > textEl.clientWidth + 1 ||
    textEl.scrollWidth > available + 1
}

let inspectorRefResizeObserver: ResizeObserver | null = null

function setupInspectorRefOverflowObserver(): void {
  nextTick(() => {
    inspectorRefResizeObserver?.disconnect()
    inspectorRefResizeObserver = null

    const textEl = infoInspectorRefTextRef.value
    const panelEl = panelRef.value
    if (!textEl) {
      infoInspectorRefIsOverflowing.value = false
      return
    }

    inspectorRefResizeObserver = new ResizeObserver(() => {
      syncInspectorRefOverflow()
    })
    inspectorRefResizeObserver.observe(textEl)
    if (panelEl) inspectorRefResizeObserver.observe(panelEl)
    syncInspectorRefOverflow()
  })
}

const infoInspectorMuted = computed(() => {
  if (infoInspectorShowsAssigned.value) return false
  return isPendingAssignmentLabel(infoInspector.value)
})

const infoCanSchedule = computed(() => detailOrderAction('schedule'))

const infoCanReschedule = computed(() => detailOrderAction('reschedule'))

const infoFollowUpTime = computed(() => {
  const fu = detailFollowUp.value
  if (!fu) return 'N/A'
  const timeText = fu.follow_up_time_text?.trim()
  if (timeText) {
    if (/\bpending\s+(schedule|reschedule)\b/i.test(timeText)) return timeText
    const formatted = formatOrderScheduleLineDisplay(timeText)
    return formatted !== '—' ? formatted : timeText
  }
  const booked = bookedTimeFromFollowUpRow(fu)
  if (booked && isBookedScheduleDisplayLabel(booked)) {
    const formatted = formatOrderScheduleLineDisplay(booked)
    return formatted !== '—' ? formatted : booked
  }
  if (isInfoFuPendingReschedule.value) return PENDING_RESCHEDULE_TIME_LABEL
  if (isInfoFuPendingSchedule.value) return PENDING_SCHEDULE_TIME_LABEL
  const status = (fu.status_text || fu.status || '').trim()
  return status || 'Pending Schedule'
})

const infoFollowUpTimeMuted = computed(() =>
  !(isInfoFuAssigned.value || isInfoFuCompleted.value) &&
  /\bpending\s+(schedule|reschedule)\b/i.test(infoFollowUpTime.value),
)

const infoFollowUpTechnician = computed(() => {
  const fu = detailFollowUp.value
  if (!fu) return 'N/A'
  const name = detailFollowUpTechnicianName(fu)
  return infoDisplay(name || 'Pending Assignment')
})

const infoFollowUpTechnicianDisplay = computed(() => infoFollowUpTechnician.value)

const infoFollowUpTechnicianMuted = computed(() =>
  isPendingAssignmentLabel(infoFollowUpTechnicianDisplay.value),
)

const infoCanScheduleFollowUp = computed(() => detailFollowUpAction('schedule'))

const infoCanRescheduleFollowUp = computed(() => {
  if (detailFollowUpAction('reschedule')) return true
  return isInfoFuAssigned.value
})

const infoPayments = computed((): CareOrderPayment[] => normalizeCareOrderPayments(detail.value))

const selectedPaymentIndex = ref(0)

watch(
  infoPayments,
  (list) => {
    if (!list.length) {
      selectedPaymentIndex.value = 0
      return
    }
    selectedPaymentIndex.value = list.length - 1
  },
  { immediate: true },
)

const selectedPayment = computed((): CareOrderPayment | null => {
  const list = infoPayments.value
  if (!list.length) return null
  const idx = Math.min(Math.max(selectedPaymentIndex.value, 0), list.length - 1)
  return list[idx] ?? null
})

const infoShowPaymentTabs = computed(() => infoPayments.value.length > 1)

const infoPaymentShowsAttempt = computed(() => {
  const pay = selectedPayment.value
  if (pay) return careOrderPaymentShowsAttempt(pay)
  const topId = pickDetailStringField('transaction_id', 'payment_id', 'txn_id')
  if (topId) return false
  const status = pickDetailStringField(
    'payment_status_text',
    'status_text',
    'payment_status',
    'status',
  )
  return !!status && /fail|due|unpaid/i.test(status)
})

const infoPaymentAttemptLabel = computed(() => {
  const pay = selectedPayment.value
  if (pay) return careOrderPaymentAttemptLabel(pay)
  const status = pickDetailStringField(
    'payment_status_text',
    'status_text',
    'payment_status',
    'status',
  )
  if (status) {
    if (/fail/i.test(status) && !/payment\s*fail/i.test(status)) return 'Payment Failed'
    return status
  }
  return 'Payment Failed'
})

const infoShowTransactionId = computed(() => {
  const pay = selectedPayment.value
  if (pay) {
    return careOrderPaymentIsSuccess(pay) && !!careOrderPaymentTransactionId(pay)
  }
  return !!pickDetailStringField('transaction_id', 'payment_id', 'txn_id')
})

const infoOrderCanRefund = computed(() => detailOrderAction('refund'))

/** 失败支付也展示 Refund，但禁用为灰色 */
const infoShowRefund = computed(
  () => infoOrderCanRefund.value || infoPaymentShowsAttempt.value,
)

const infoRefundEnabled = computed(() => {
  if (!infoOrderCanRefund.value) return false
  if (infoPaymentShowsAttempt.value) return false
  const pay = selectedPayment.value
  if (pay) return careOrderPaymentIsSuccess(pay)
  return true
})

const infoTransactionId = computed(() => {
  const pay = selectedPayment.value
  if (pay) return infoDisplay(careOrderPaymentTransactionId(pay))
  return infoDisplay(pickDetailStringField('transaction_id', 'payment_id', 'txn_id'))
})

const infoSubtotal = computed(() => {
  const pay = selectedPayment.value
  if (pay && pay.subtotal_amount != null) return formatInfoMoney(pay.subtotal_amount)
  return formatInfoMoney(detail.value?.subtotal_amount)
})

const infoCoupon = computed(() => {
  const pay = selectedPayment.value
  if (pay) {
    const fromPay = pickCouponDiscountFromRecord(pay as Record<string, unknown>)
    if (fromPay) return infoDisplay(fromPay)
  }
  return infoDisplay(pickDetailCouponDiscount())
})

const infoTaxLabel = computed(() => {
  const pay = selectedPayment.value
  const rate = pay?.tax_rate ?? detail.value?.tax_rate
  const pct =
    rate != null && Number.isFinite(rate)
      ? Math.round(rate <= 1 ? rate * 100 : rate)
      : 13
  return `Tax(${pct}%):`
})

const infoTaxAmount = computed(() => {
  const pay = selectedPayment.value
  if (pay && pay.tax_amount != null) return formatInfoMoney(pay.tax_amount)
  return formatInfoMoney(detail.value?.tax_amount)
})

const infoTotal = computed(() => {
  const pay = selectedPayment.value
  if (pay) {
    const text = pay.total_amount_text?.trim()
    if (text && text !== '—') return text
    if (pay.total_amount != null) return formatInfoMoney(pay.total_amount)
  }
  const text = detail.value?.total_amount_text?.trim()
  if (text && text !== '—') return text
  return formatInfoMoney(detail.value?.total_amount)
})

const infoPlacedOn = computed(() => {
  const pay = selectedPayment.value
  const raw =
    pay?.placed_at?.trim() ||
    detail.value?.placed_at?.trim() ||
    detail.value?.create_time?.trim() ||
    ''
  if (!raw) return 'N/A'
  const formatted = formatOrderScheduleLineDisplay(raw)
  return formatted !== '—' ? formatted : raw
})

const infoPaidOn = computed(() => {
  const pay = selectedPayment.value
  const raw = pay
    ? (pay.paid_at?.trim() ||
        (typeof pay.payment_at === 'string' ? pay.payment_at.trim() : '') ||
        (typeof pay.paid_time === 'string' ? pay.paid_time.trim() : ''))
    : detail.value?.paid_at?.trim() || pickDetailStringField('payment_at', 'paid_time')
  if (!raw) return 'N/A'
  const formatted = formatOrderScheduleLineDisplay(raw)
  return formatted !== '—' ? formatted : raw
})

const infoPaymentMethod = computed(() => {
  const pay = selectedPayment.value
  if (pay?.payment_method?.trim()) return infoDisplay(pay.payment_method)
  return infoDisplay(detail.value?.payment_method)
})

function selectInfoPayment(index: number) {
  if (index < 0 || index >= infoPayments.value.length) return
  selectedPaymentIndex.value = index
}

const infoNotesText = computed(() => {
  const v = detail.value?.notes?.trim()
  return v || 'N/A'
})

function onInfoAddNoteClick() {
  if (props.order) emit('add-note', props.order)
}

const infoTimeline = computed((): InfoTimelineEvent[] => {
  const list = detail.value?.timeline
  if (!Array.isArray(list) || !list.length) return []
  const events: InfoTimelineEvent[] = []
  for (const item of list) {
    if (!item || typeof item !== 'object') continue
    const mapped = mapCareOrderTimelineEvent(item as CareOrderTimelineEvent)
    if (mapped) events.push(mapped)
  }
  return events
})

function onInfoSendMessages() {
  /* 设计稿常亮占位，与列表 Send Messages 一致 */
}

async function onRegisteredMobileClick(): Promise<void> {
  if (!hasRegisteredMobile.value) return
  const mobile = infoRegisteredMobileRaw.value.trim()
  const userId = resolveCareOrderDisplayUserId(detail.value ?? undefined).trim()
  if (!mobile) {
    showToast?.('warning', 'Mobile not found.')
    return
  }
  const pk = await resolveUserDetailPk({
    displayUserId: userId,
    mobile,
  })
  if (!pk) {
    showToast?.('warning', 'User not found.')
    return
  }
  writeUsersListPrefill({
    searchType: 'mobile_no',
    keyword: normalizePhoneSearchDigits(mobile) || mobile,
  })
  void router.push(usersDetailRoute(pk))
}

function onInfoSchedule() {
  if (props.order) emit('schedule', props.order)
}

function onInfoReschedule() {
  if (props.order) emit('reschedule', props.order)
}

function onInfoRefund() {
  if (!props.order || !infoRefundEnabled.value) return
  emit('refund', props.order)
}

function summaryToneClass(token: string): string {
  return productCodeTagToneClass(token, 'cod')
}

function statusIconSrc(tone: CareOrderChecklistStatusTone): string {
  switch (tone) {
    case 'attention':
      return imgAlert
    case 'normal':
      return imgNormal
    case 'major':
    case 'major-covered':
      return imgErrorRed
    case 'unable':
      return imgUnable
    case 'pending':
    default:
      return imgPending
  }
}

const panelRef = ref<HTMLElement | null>(null)
const infoSubscriptionLabelRef = ref<HTMLElement | null>(null)
const infoSubscriptionLabelWidthPx = ref('118px')

/** 列宽只量 Subscription ID：其它小标题右对齐后冒号与其对齐 */
function syncInfoSubscriptionLabelWidth(): void {
  nextTick(() => {
    const w = infoSubscriptionLabelRef.value?.offsetWidth
    if (w && w > 0) infoSubscriptionLabelWidthPx.value = `${w}px`
  })
}

function close() {
  selectedChecklistRow.value = null
  emit('close')
}

function scrollDetailToTop(): void {
  const panel = panelRef.value
  if (!panel) return

  const scrollEl =
    (panel.querySelector('.cod-main') as HTMLElement | null) ??
    (panel.closest('.cod-overlay-shell') as HTMLElement | null) ??
    (panel.closest('.cod-page-shell') as HTMLElement | null)

  const apply = () => {
    if (scrollEl) scrollEl.scrollTop = 0
  }

  apply()
  requestAnimationFrame(apply)
}

async function scrollSidePanelToTopAfterRender(): Promise<void> {
  await nextTick()
  await nextTick()
  scrollDetailToTop()
}

async function loadDetail() {
  const order = props.order
  if (!order) {
    detail.value = null
    return
  }
  loadError.value = ''
  if (order.orderPrimaryId == null) {
    detail.value = null
    return
  }
  loading.value = true
  try {
    detail.value = await postCareOrderDetail(order.orderPrimaryId)
  } catch (e) {
    detail.value = null
    loadError.value = e instanceof Error ? e.message : 'Failed to load order detail.'
  } finally {
    loading.value = false
    if (!selectedChecklistRow.value) {
      void scrollDetailToTopAfterRender()
    }
  }
}

async function scrollDetailToTopAfterRender(): Promise<void> {
  await nextTick()
  await nextTick()
  scrollDetailToTop()
}

function onChecklistDetailBack() {
  selectedChecklistRow.value = null
}

function onItemViewDetails(row: CareOrderChecklistDisplayRow) {
  if (
    row.statusTone === 'attention' ||
    row.statusTone === 'normal' ||
    row.statusTone === 'unable' ||
    row.statusTone === 'pending' ||
    row.statusTone === 'major' ||
    row.statusTone === 'major-covered'
  ) {
    selectedChecklistRow.value = row
    void scrollSidePanelToTopAfterRender()
    return
  }
  void createFollowUpFromChecklistRow(row)
}

async function createFollowUpFromChecklistRow(row: CareOrderChecklistDisplayRow) {
  if (followUpSubmitting.value) return

  if (!canCreateFollowUpFromChecklistRow(row)) {
    if (row.statusTone === 'pending' || row.statusTone === 'normal') return
    showToast?.(
      'warning',
      row.orderItemId == null
        ? 'Cannot create follow-up: checklist item id is missing.'
        : 'This item does not require a follow-up.',
    )
    return
  }

  followUpSubmitting.value = true
  try {
    await postCareOrderCreateFollowUp({
      order_item_id: row.orderItemId!,
      issue_level: issueLevelFromChecklistStatusTone(row.statusTone),
      notes: buildFollowUpNotesFromChecklistRow(row),
    })
    showToast?.('success', 'Follow-up created successfully.')
    emit('ordersRefresh')
    if (props.order?.orderPrimaryId != null) {
      await loadDetail()
    }
  } catch (e) {
    showToast?.(
      'error',
      e instanceof Error ? e.message : 'Failed to create follow-up.',
    )
  } finally {
    followUpSubmitting.value = false
  }
}

function onServiceTimeTipScrollOrResize(): void {
  if (serviceTimeTipVisible.value) updateServiceTimeTipPosition()
  if (followUpTimeTipVisible.value) updateFollowUpTimeTipPosition()
  syncInfoSubscriptionLabelWidth()
  syncInspectorRefOverflow()
}

onMounted(() => {
  activeTab.value = 'information'
  scrollDetailToTop()
  void loadDetail()
  syncInfoSubscriptionLabelWidth()
  setupInspectorRefOverflowObserver()
  window.addEventListener('scroll', onServiceTimeTipScrollOrResize, true)
  window.addEventListener('resize', onServiceTimeTipScrollOrResize)
})

onBeforeUnmount(() => {
  inspectorRefResizeObserver?.disconnect()
  inspectorRefResizeObserver = null
  window.removeEventListener('scroll', onServiceTimeTipScrollOrResize, true)
  window.removeEventListener('resize', onServiceTimeTipScrollOrResize)
})

function checklistRowScrollKey(row: CareOrderChecklistDisplayRow | null): string {
  if (!row) return ''
  return `${row.orderItemId ?? ''}|${row.title}|${row.statusTone}`
}

watch(selectedChecklistRow, (row, prev) => {
  if (!row) return
  if (checklistRowScrollKey(row) !== checklistRowScrollKey(prev)) {
    void scrollSidePanelToTopAfterRender()
  }
})

watch(activeTab, () => {
  void scrollDetailToTopAfterRender()
  syncInfoSubscriptionLabelWidth()
  if (activeTab.value === 'information') {
    nextTick(() => setupInspectorRefOverflowObserver())
  }
})

watch(loading, (isLoading) => {
  if (!isLoading) {
    syncInfoSubscriptionLabelWidth()
    nextTick(() => setupInspectorRefOverflowObserver())
  }
})

watch(infoInspectorRefDisplay, () => {
  nextTick(() => setupInspectorRefOverflowObserver())
})

watch(infoInspectorShowsAssigned, () => {
  nextTick(() => setupInspectorRefOverflowObserver())
})

watch(
  () => props.order?.id,
  () => {
    activeTab.value = 'information'
    selectedChecklistRow.value = null
    scrollDetailToTop()
    void loadDetail()
  },
)
</script>

<template>
  <div v-if="order" ref="panelRef" class="cod-panel" aria-labelledby="cod-panel-title">
    <header class="cod-head">
      <h2 id="cod-panel-title">HomeMetric Care Order Detail</h2>
      <button type="button" class="cod-x" aria-label="Close" @click="close">
        <img :src="imgClose" class="cod-x-ic" alt="" />
      </button>
    </header>

    <div class="cod-main">
      <div
        v-if="selectedChecklistRow"
        class="cod-inline-detail"
      >
        <CareOrderChecklistItemDetailView
          :key="`${selectedChecklistRow.orderItemId ?? ''}-${selectedChecklistRow.title}-${selectedChecklistRow.statusTone}`"
          :row="selectedChecklistRow"
          :order-detail="detail"
          :order-card="order"
          @back="onChecklistDetailBack"
        />
      </div>
      <template v-else>
          <p v-if="loadError" class="cod-error">{{ loadError }}</p>
          <LoadingIndicator v-else-if="loading" min-height="320px" />
          <template v-else>
            <div class="cod-tabs">
              <button
                type="button"
                class="cod-tab"
                :class="{ active: activeTab === 'information' }"
                @click="activeTab = 'information'"
              >
                Information
              </button>
              <button
                type="button"
                class="cod-tab"
                :class="{ active: activeTab === 'checklist' }"
                @click="activeTab = 'checklist'"
              >
                Checklist and Follow-up
              </button>
            </div>

            <div v-show="activeTab === 'information'" class="cod-body-inner cod-info">
              <section class="cod-info-section">
                <div class="cod-info-row cod-info-row--subscription-id">
                  <span ref="infoSubscriptionLabelRef" class="cod-info-label">Subscription ID:</span>
                  <span class="cod-info-value">{{ infoSubscriptionId }}</span>
                </div>
                <div class="cod-info-row cod-info-row--order-id">
                  <span class="cod-info-label">Order ID:</span>
                  <span class="cod-info-value">{{ infoOrderId }}</span>
                </div>
                <div class="cod-info-row cod-info-row--home-id">
                  <span class="cod-info-label">User ID:</span>
                  <span class="cod-info-value">{{ infoUserId }}</span>
                </div>
                <div class="cod-info-row cod-info-row--action cod-info-row--registered-mobile">
                  <span class="cod-info-label">Registered Mobile:</span>
                  <button
                    v-if="hasRegisteredMobile"
                    type="button"
                    class="cod-info-value cod-info-value--mobile cod-info-value--mobile-link"
                    @click="onRegisteredMobileClick"
                  >
                    {{ infoRegisteredMobile }}
                  </button>
                  <span
                    v-else
                    class="cod-info-value cod-info-value--mobile"
                  >{{ infoRegisteredMobile }}</span>
                  <button
                    type="button"
                    class="cod-info-action cod-info-action--message"
                    @click="onInfoSendMessages"
                  >
                    <img :src="imgMessage" class="cod-info-action-ic" alt="" aria-hidden="true" />
                    <span class="cod-info-action-txt">Send Messages</span>
                  </button>
                </div>
                <div class="cod-info-row cod-info-row--service-status">
                  <span class="cod-info-label">Service Status:</span>
                  <span class="cod-info-value">{{ infoServiceStatus }}</span>
                </div>
                <div class="cod-info-row cod-info-row--care-plan">
                  <span class="cod-info-label">Care Plan:</span>
                  <span class="cod-info-value">{{ infoCarePlan }}</span>
                </div>
                <div class="cod-info-row cod-info-row--service-cycle">
                  <span class="cod-info-label">Service Cycle:</span>
                  <span class="cod-info-value">{{ infoServiceCycle }}</span>
                </div>
                <div class="cod-info-row cod-info-row--service-address">
                  <span class="cod-info-label">Service Address:</span>
                  <span class="cod-info-value">{{ infoServiceAddress }}</span>
                </div>
              </section>

              <div class="cod-info-divider cod-info-divider--after-service-address" aria-hidden="true" />

              <section class="cod-info-section cod-info-section--contact">
                <div class="cod-info-row cod-info-row--contact cod-info-row--contact-person">
                  <span class="cod-info-label">Contact Person:</span>
                  <span class="cod-info-value cod-info-value--contact">{{ infoContactPerson }}</span>
                </div>
                <div class="cod-info-row cod-info-row--contact">
                  <span class="cod-info-label">Phone Number:</span>
                  <span class="cod-info-value cod-info-value--contact">{{ infoPhoneNumber }}</span>
                </div>
              </section>

              <div class="cod-info-divider" aria-hidden="true" />

              <section class="cod-info-section cod-info-section--inspection">
                <div class="cod-info-row">
                  <span class="cod-info-label">Check Summary:</span>
                  <span v-if="infoCheckSummaryTokens.length" class="cod-info-value cod-info-check-summary">
                    <template v-for="(token, i) in infoCheckSummaryTokens" :key="`${token}-${i}`">
                      <span v-if="i > 0" class="cod-info-check-summary-sep" aria-hidden="true"></span>
                      <span :class="['cod-summary-token', summaryToneClass(token)]">{{ token }}</span>
                    </template>
                  </span>
                  <span v-else class="cod-info-value">N/A</span>
                </div>
                <div class="cod-info-divider cod-info-divider--after-check-summary" aria-hidden="true" />
                <div class="cod-info-row cod-info-row--action cod-info-row--fu-service-time">
                  <span class="cod-info-label">Service Time:</span>
                  <template v-if="isInfoMainPendingReschedule">
                    <div class="cod-info-service-time">
                      <span
                        class="cod-info-value cod-info-value--service-time"
                        :class="{ 'cod-info-value--muted': infoServiceTimeMuted }"
                      >{{ infoServiceTime }}</span>
                      <span
                        v-if="infoServiceTimeTipVisible"
                        ref="serviceTimeTipHostRef"
                        class="cod-info-preferred-host"
                        @mouseenter="showServiceTimeTip"
                        @mouseleave="hideServiceTimeTip"
                        @focusin="showServiceTimeTip"
                        @focusout="hideServiceTimeTip"
                      >
                        <button type="button" class="cod-info-preferred-btn" aria-label="Preferred times">
                          <img :src="imgRescheduleGlyph" class="cod-info-preferred-ic" alt="" aria-hidden="true" />
                        </button>
                        <Teleport to="body">
                          <div
                            v-show="serviceTimeTipVisible"
                            class="cod-info-preferred-tip"
                            role="tooltip"
                            :style="serviceTimeTipStyle"
                          >
                            <div class="cod-info-preferred-tip-title">Preferred Time Slots:</div>
                            <ul class="cod-info-preferred-tip-list">
                              <li v-for="(pt, pti) in infoPreferredTimeTipEntries" :key="pti">{{ pt }}</li>
                            </ul>
                          </div>
                        </Teleport>
                      </span>
                    </div>
                    <button
                      v-if="infoCanReschedule"
                      type="button"
                      class="cod-info-action cod-info-action--reschedule"
                      @click="onInfoReschedule"
                    >
                      <img :src="imgSchedule" class="cod-info-action-ic" alt="" aria-hidden="true" />
                      <span class="cod-info-action-txt">Reschedule</span>
                    </button>
                  </template>
                  <template v-else-if="isInfoMainAssigned">
                    <span class="cod-info-value">{{ infoServiceTime }}</span>
                    <button
                      v-if="infoCanReschedule"
                      type="button"
                      class="cod-info-action cod-info-action--reschedule"
                      @click="onInfoReschedule"
                    >
                      <img :src="imgSchedule" class="cod-info-action-ic" alt="" aria-hidden="true" />
                      <span class="cod-info-action-txt">Reschedule</span>
                    </button>
                  </template>
                  <template v-else-if="isInfoMainCompleted">
                    <span class="cod-info-value cod-info-value--fu-service-time">{{ infoServiceTime }}</span>
                    <button
                      type="button"
                      class="cod-info-action cod-info-action--reschedule cod-info-action--disabled"
                      disabled
                      aria-disabled="true"
                    >
                      <img :src="imgSchedule" class="cod-info-action-ic" alt="" aria-hidden="true" />
                      <span class="cod-info-action-txt">Reschedule</span>
                    </button>
                  </template>
                  <template v-else-if="isInfoFuCompleted">
                    <span class="cod-info-value cod-info-value--fu-service-time">{{ infoServiceTime }}</span>
                    <button
                      type="button"
                      class="cod-info-action cod-info-action--reschedule cod-info-action--disabled"
                      disabled
                      aria-disabled="true"
                    >
                      <img :src="imgSchedule" class="cod-info-action-ic" alt="" aria-hidden="true" />
                      <span class="cod-info-action-txt">Reschedule</span>
                    </button>
                  </template>
                  <template v-else-if="isInfoFuFollowUpBlock">
                    <span class="cod-info-value cod-info-value--fu-service-time">{{ infoServiceTime }}</span>
                    <button
                      type="button"
                      class="cod-info-action cod-info-action--reschedule cod-info-action--disabled"
                      disabled
                      aria-disabled="true"
                    >
                      <img :src="imgSchedule" class="cod-info-action-ic" alt="" aria-hidden="true" />
                      <span class="cod-info-action-txt">Reschedule</span>
                    </button>
                  </template>
                  <template v-else>
                    <span
                      class="cod-info-value"
                      :class="{ 'cod-info-value--muted': infoServiceTimeMuted }"
                    >{{ infoServiceTime }}</span>
                    <button
                      v-if="infoCanSchedule"
                      type="button"
                      class="cod-info-action cod-info-action--schedule"
                      @click="onInfoSchedule"
                    >
                      <img :src="imgSchedule" class="cod-info-action-ic" alt="" aria-hidden="true" />
                      <span class="cod-info-action-txt">Schedule</span>
                    </button>
                  </template>
                </div>
                <div class="cod-info-row">
                  <span class="cod-info-label">{{ infoAssigneeRoleLabel }}:</span>
                  <div v-if="infoInspectorShowsAssigned" class="cod-info-inspector">
                    <span class="cod-info-inspector-name">{{ infoInspectorName }}</span>
                    <AssignNameTipHost
                      v-if="infoInspectorRefDisplay"
                      :text="infoInspectorRefTipText"
                      placement="bottom"
                      class="cod-info-inspector-ref-tip-host"
                    >
                      <span class="cod-info-inspector-ref">
                        <img :src="imgNormal" class="cod-info-inspector-ref-ic" alt="" aria-hidden="true" />
                        <span ref="infoInspectorRefTextRef" class="cod-info-inspector-ref-text">
                          {{ infoInspectorRefDisplay }}
                        </span>
                      </span>
                    </AssignNameTipHost>
                  </div>
                  <span
                    v-else
                    class="cod-info-value"
                    :class="{ 'cod-info-value--muted': infoInspectorMuted }"
                  >{{ infoInspector }}</span>
                </div>
                <div
                  v-if="isInfoFuAssigned || isInfoFuCompleted"
                  class="cod-info-divider cod-info-divider--after-inspector"
                  aria-hidden="true"
                />
                <template v-if="isInfoFuAssigned || isInfoFuCompleted">
                  <div class="cod-info-row cod-info-row--action cod-info-row--fu-followup-time">
                    <span class="cod-info-label">Follow-up Time:</span>
                    <span class="cod-info-value">{{ infoFollowUpTime }}</span>
                    <button
                      v-if="isInfoFuAssigned"
                      type="button"
                      class="cod-info-action cod-info-action--reschedule"
                      @click="onInfoReschedule"
                    >
                      <img :src="imgSchedule" class="cod-info-action-ic" alt="" aria-hidden="true" />
                      <span class="cod-info-action-txt">Reschedule</span>
                    </button>
                    <button
                      v-else
                      type="button"
                      class="cod-info-action cod-info-action--reschedule cod-info-action--disabled"
                      disabled
                      aria-disabled="true"
                    >
                      <img :src="imgSchedule" class="cod-info-action-ic" alt="" aria-hidden="true" />
                      <span class="cod-info-action-txt">Reschedule</span>
                    </button>
                  </div>
                  <div class="cod-info-row">
                    <span class="cod-info-label">Technician:</span>
                    <span
                      class="cod-info-value"
                      :class="{ 'cod-info-value--muted': infoFollowUpTechnicianMuted }"
                    >{{ infoFollowUpTechnicianDisplay }}</span>
                  </div>
                </template>
                <template v-else-if="isInfoFuContext">
                  <div
                    class="cod-info-divider cod-info-divider--after-inspector"
                    aria-hidden="true"
                  />
                  <div class="cod-info-row cod-info-row--action">
                    <span class="cod-info-label">Follow-up Time:</span>
                    <div v-if="infoFollowUpPreferredTimeTipVisible" class="cod-info-service-time">
                      <span
                        class="cod-info-value cod-info-value--service-time"
                        :class="{ 'cod-info-value--muted': infoFollowUpTimeMuted }"
                      >{{ infoFollowUpTime }}</span>
                      <span
                        ref="followUpTimeTipHostRef"
                        class="cod-info-preferred-host"
                        @mouseenter="showFollowUpTimeTip"
                        @mouseleave="hideFollowUpTimeTip"
                        @focusin="showFollowUpTimeTip"
                        @focusout="hideFollowUpTimeTip"
                      >
                        <button type="button" class="cod-info-preferred-btn" aria-label="Preferred times">
                          <img :src="imgRescheduleGlyph" class="cod-info-preferred-ic" alt="" aria-hidden="true" />
                        </button>
                        <Teleport to="body">
                          <div
                            v-show="followUpTimeTipVisible"
                            class="cod-info-preferred-tip"
                            role="tooltip"
                            :style="followUpTimeTipStyle"
                          >
                            <div class="cod-info-preferred-tip-title">Preferred Time Slots:</div>
                            <ul class="cod-info-preferred-tip-list">
                              <li v-for="(pt, pti) in infoPreferredTimeTipEntries" :key="pti">{{ pt }}</li>
                            </ul>
                          </div>
                        </Teleport>
                      </span>
                    </div>
                    <span
                      v-else
                      class="cod-info-value"
                      :class="{ 'cod-info-value--muted': infoFollowUpTimeMuted }"
                    >{{ infoFollowUpTime }}</span>
                    <button
                      v-if="isInfoFuPendingSchedule && infoCanScheduleFollowUp"
                      type="button"
                      class="cod-info-action cod-info-action--schedule"
                      @click="onInfoSchedule"
                    >
                      <img :src="imgSchedule" class="cod-info-action-ic" alt="" aria-hidden="true" />
                      <span class="cod-info-action-txt">Schedule</span>
                    </button>
                    <button
                      v-if="isInfoFuPendingReschedule && infoCanRescheduleFollowUp"
                      type="button"
                      class="cod-info-action cod-info-action--reschedule"
                      @click="onInfoReschedule"
                    >
                      <img :src="imgSchedule" class="cod-info-action-ic" alt="" aria-hidden="true" />
                      <span class="cod-info-action-txt">Reschedule</span>
                    </button>
                  </div>
                  <div class="cod-info-row">
                    <span class="cod-info-label">Technician:</span>
                    <span
                      class="cod-info-value"
                      :class="{ 'cod-info-value--muted': infoFollowUpTechnicianMuted }"
                    >{{ infoFollowUpTechnician }}</span>
                  </div>
                </template>
              </section>

              <div class="cod-info-divider" aria-hidden="true" />

              <section class="cod-info-section cod-info-section--financial">
                <div v-if="infoShowPaymentTabs" class="cod-info-payment-tabs" role="tablist">
                  <button
                    v-for="(pay, idx) in infoPayments"
                    :key="`payment-tab-${idx}`"
                    type="button"
                    role="tab"
                    class="cod-info-payment-tab"
                    :class="{ 'cod-info-payment-tab--active': idx === selectedPaymentIndex }"
                    :aria-selected="idx === selectedPaymentIndex"
                    @click="selectInfoPayment(idx)"
                  >
                    Payment - {{ idx + 1 }}
                  </button>
                </div>
                <div v-if="infoPaymentShowsAttempt" class="cod-info-row cod-info-row--payment-attempt">
                  <span class="cod-info-label">Payment attempt:</span>
                  <span class="cod-info-payment-attempt-badge">{{ infoPaymentAttemptLabel }}</span>
                </div>
                <div v-if="infoShowTransactionId" class="cod-info-row">
                  <span class="cod-info-label">Transaction ID:</span>
                  <span class="cod-info-value">{{ infoTransactionId }}</span>
                </div>
                <div class="cod-info-row">
                  <span class="cod-info-label">Subtotal:</span>
                  <span class="cod-info-value">{{ infoSubtotal }}</span>
                </div>
                <div class="cod-info-row">
                  <span class="cod-info-label">Coupon:</span>
                  <span class="cod-info-value">{{ infoCoupon }}</span>
                </div>
                <div class="cod-info-row">
                  <span class="cod-info-label">{{ infoTaxLabel }}</span>
                  <span class="cod-info-value">{{ infoTaxAmount }}</span>
                </div>
                <div class="cod-info-row">
                  <span class="cod-info-label">Total:</span>
                  <span class="cod-info-value">{{ infoTotal }}</span>
                </div>
                <div class="cod-info-row cod-info-row--action">
                  <span class="cod-info-label">Placed on:</span>
                  <span class="cod-info-value">{{ infoPlacedOn }}</span>
                  <button
                    v-if="infoShowRefund"
                    type="button"
                    class="cod-info-action cod-info-action--refund"
                    :class="{ 'cod-info-action--disabled': !infoRefundEnabled }"
                    :disabled="!infoRefundEnabled"
                    @click="onInfoRefund"
                  >
                    <img :src="imgRefund" class="cod-info-action-ic" alt="" aria-hidden="true" />
                    <span class="cod-info-action-txt">Refund</span>
                  </button>
                </div>
                <div class="cod-info-row">
                  <span class="cod-info-label">Paid on:</span>
                  <span class="cod-info-value">{{ infoPaidOn }}</span>
                </div>
                <div class="cod-info-row cod-info-row--payment-method">
                  <span class="cod-info-label">Payment method:</span>
                  <span class="cod-info-value">{{ infoPaymentMethod }}</span>
                </div>
              </section>

              <div class="cod-info-divider" aria-hidden="true" />

              <section class="cod-info-section cod-info-section--notes">
                <div class="cod-info-row cod-info-row--top cod-info-row--notes">
                  <span class="cod-info-label">Notes:</span>
                  <div class="cod-info-notes">
                    <p class="cod-info-notes-text">{{ infoNotesText }}</p>
                    <button type="button" class="cod-info-action cod-info-action--notes" @click="onInfoAddNoteClick">
                      <img :src="imgNote" class="cod-info-action-ic" alt="" aria-hidden="true" />
                      <span class="cod-info-action-txt">Add notes</span>
                    </button>
                  </div>
                </div>
              </section>

              <div class="cod-info-divider" aria-hidden="true" />

              <section class="cod-info-section">
                <div class="cod-info-row cod-info-row--top cod-info-row--timeline">
                  <span class="cod-info-label">Timeline:</span>
                  <div
                    v-if="infoTimeline.length"
                    class="cod-info-timeline"
                  >
                    <div
                      v-for="(ev, i) in infoTimeline"
                      :key="`${ev.label}-${i}`"
                      class="cod-info-timeline-item"
                    >
                      <span class="cod-info-timeline-at">{{ ev.at }}</span>
                      <div v-if="ev.actor" class="cod-info-timeline-body">
                        <span class="cod-info-timeline-label">{{ ev.label }}</span>
                        <span class="cod-info-timeline-actor">By {{ ev.actor }}</span>
                      </div>
                      <span v-else class="cod-info-timeline-label">{{ ev.label }}</span>
                    </div>
                  </div>
                  <span v-else class="cod-info-value">N/A</span>
                </div>
              </section>
            </div>

            <div v-show="activeTab === 'checklist'" class="cod-body-inner">
              <div v-if="summaryTokens.length" class="cod-summary">
                <template v-for="(token, i) in summaryTokens" :key="`${token}-${i}`">
                  <span v-if="i > 0" class="cod-summary-sep" aria-hidden="true"></span>
                  <span :class="['cod-summary-token', summaryToneClass(token)]">{{ token }}</span>
                </template>
              </div>

              <ul v-if="checklistRows.length" class="cod-checklist">
                <li
                  v-for="(row, idx) in checklistRows"
                  :key="`${row.title}-${idx}`"
                  class="cod-check-item"
                  :class="{ 'cod-check-item--selected': selectedChecklistRow === row }"
                >
                  <div class="cod-check-top">
                    <h3 class="cod-check-title">{{ row.title }}</h3>
                    <span
                      class="cod-type-tag"
                      :class="
                        row.typeTag === 'Baseline' ? 'cod-type-tag--baseline' : 'cod-type-tag--routine'
                      "
                    >
                      {{ row.typeLabel ?? row.typeTag }}
                    </span>
                  </div>
                  <div class="cod-check-status" :class="`cod-check-status--${row.statusTone}`">
                    <img
                      :src="statusIconSrc(row.statusTone)"
                      class="cod-status-ico"
                      alt=""
                      aria-hidden="true"
                    />
                    <span class="cod-status-label">{{ row.statusLabel }}</span>
                  </div>
                  <div class="cod-check-foot">
                    <span class="cod-check-loc">{{ row.location }}</span>
                    <button
                      type="button"
                      class="cod-check-link"
                      :disabled="followUpSubmitting"
                      @click="onItemViewDetails(row)"
                    >
                      View Details
                      <img
                        :src="imgRightArrow"
                        class="cod-check-chev"
                        alt=""
                        aria-hidden="true"
                      />
                    </button>
                  </div>
                </li>
              </ul>
              <p v-else class="cod-empty">No checklist items for this order.</p>
            </div>
          </template>
      </template>
    </div>
  </div>
</template>

<style scoped>
.cod-panel {
  --cod-page-pad: 24px;
  width: 100%;
  max-height: inherit;
  background: #fff;
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  overflow: hidden;
  min-height: 0;
}

.cod-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  flex-shrink: 0;
  padding: var(--cod-page-pad) var(--cod-page-pad) 0;
}

.cod-head h2 {
  margin: 0;
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro', system-ui, sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0;
  color: #000000;
  text-align: left;
  flex: 1;
}

.cod-x {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 8px;
  flex-shrink: 0;
}

.cod-x-ic {
  width: 14px;
  height: 14px;
  display: block;
}

.cod-tabs {
  display: flex;
  gap: 0;
  margin: 16px var(--cod-page-pad) 16px;
  padding: 0;
  width: auto;
  max-width: calc(100% - 2 * var(--cod-page-pad));
  height: 32px;
  background: #f0f1f3;
  border-radius: 16px;
  box-sizing: border-box;
}

.cod-tab {
  flex: 1 1 50%;
  min-width: 0;
  height: 32px;
  border: none;
  background: transparent;
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro', system-ui, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: 0;
  color: #999999;
  padding: 0 12px;
  border-radius: 16px;
  cursor: pointer;
  box-sizing: border-box;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.cod-tab.active {
  background: #000000;
  font-weight: 600;
  color: #ffffff;
}

.cod-main {
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  box-sizing: border-box;
  padding-bottom: var(--cod-page-pad);
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.cod-main::-webkit-scrollbar {
  display: none;
  width: 0;
  height: 0;
}

.cod-error {
  padding: var(--cod-page-pad) var(--cod-page-pad) 0;
  font-size: 0.875rem;
  color: #666;
}

.cod-error {
  color: #e53935;
}

.cod-body-inner {
  padding: 0 var(--cod-page-pad);
  box-sizing: border-box;
  min-width: 0;
}

.cod-body-inner.cod-info {
  --cod-info-subscription-top: 112px;
  --cod-info-head-offset: 56px;
  --cod-info-tabs-gap-top: 16px;
  --cod-info-tabs-height: 32px;
  --cod-info-tabs-gap-bottom: 16px;
  --cod-info-subscription-label-width: v-bind(infoSubscriptionLabelWidthPx);
  --cod-info-row-indent: 10px;
  --cod-info-row-indent-registered-mobile: -8px;
  padding-bottom: 8px;
}

.cod-info {
  padding-bottom: 8px;
}

.cod-info-row--subscription-id {
  margin-top: calc(
    var(--cod-info-subscription-top) - var(--cod-info-head-offset) - var(--cod-info-tabs-gap-top) -
      var(--cod-info-tabs-height) - var(--cod-info-tabs-gap-bottom)
  );
  margin-left: var(--cod-info-row-indent);
}

.cod-info-row--subscription-id .cod-info-label {
  margin-left: 0;
}

.cod-info .cod-info-row:not(.cod-info-row--subscription-id):not(.cod-info-row--registered-mobile) {
  margin-left: var(--cod-info-row-indent);
}

.cod-info
  .cod-info-row:not(.cod-info-row--subscription-id):not(.cod-info-row--registered-mobile)
  .cod-info-label {
  flex: 0 0 var(--cod-info-subscription-label-width);
  width: var(--cod-info-subscription-label-width);
  min-width: 0;
  max-width: var(--cod-info-subscription-label-width);
  text-align: right;
  margin-left: 0;
  box-sizing: border-box;
}

.cod-info .cod-info-row--registered-mobile {
  margin-left: var(--cod-info-row-indent-registered-mobile);
}

.cod-info .cod-info-row--registered-mobile .cod-info-label {
  flex: 0 0
    calc(
      var(--cod-info-subscription-label-width) + var(--cod-info-row-indent) -
        var(--cod-info-row-indent-registered-mobile)
    );
  width: calc(
    var(--cod-info-subscription-label-width) + var(--cod-info-row-indent) -
      var(--cod-info-row-indent-registered-mobile)
  );
  text-align: right;
  margin-left: 0;
}

.cod-body-inner .cod-empty {
  padding: 0;
  margin: 0;
}

.cod-info-section:first-child {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-top: 0;
  padding-bottom: 0;
}

.cod-info-section {
  padding: 0;
}

.cod-info-divider + .cod-info-section {
  padding-top: 0;
  padding-bottom: 0;
}

.cod-info-divider {
  display: block;
  width: 492px;
  height: 1px;
  background: #E0E0E0;
  margin: 16px 0;
  border: none;
  box-sizing: border-box;
}

.cod-info-divider--after-service-address {
  margin-top: 16px;
  margin-bottom: 16px;
}

.cod-info-divider--after-check-summary {
  margin-top: 16px;
  margin-bottom: 16px;
}

.cod-info-divider--after-inspector {
  margin-top: 16px;
  margin-bottom: 16px;
}

.cod-info-section--contact + .cod-info-divider {
  margin-top: 16px;
  margin-bottom: 16px;
}

.cod-info-divider + .cod-info-section--notes {
  padding-top: 0;
  padding-bottom: 0;
}

.cod-info-section--notes + .cod-info-divider {
  margin-top: 16px;
  margin-bottom: 16px;
}

.cod-info-row {
  display: flex;
  align-items: center;
  gap: 24px;
  min-width: 0;
}

.cod-info-row + .cod-info-row {
  margin-top: 16px;
}

.cod-info-section:first-child .cod-info-row + .cod-info-row {
  margin-top: 0;
}

.cod-info-row--top {
  align-items: flex-start;
}

.cod-info-row--action {
  flex-wrap: wrap;
  row-gap: 8px;
}

.cod-info-row--fu-service-time,
.cod-info-row--fu-followup-time {
  flex-wrap: nowrap;
}

.cod-info-value--fu-service-time {
  flex: 1 1 auto;
  min-width: 0;
}

.cod-info-label {
  flex: 0 0 auto;
  box-sizing: border-box;
  font-family: SFPro, SFPro;
  font-weight: 400;
  font-size: 14px;
  color: #000000;
  line-height: 20px;
  text-align: center;
  font-style: normal;
  white-space: nowrap;
}

.cod-info-value,
.cod-info-notes,
.cod-info-timeline {
  flex: 1 1 auto;
  min-width: 0;
}

.cod-info-value {
  font-family: SFPro, SFPro;
  font-weight: 400;
  font-size: 14px;
  color: #000000;
  line-height: 20px;
  text-align: left;
  font-style: normal;
  word-break: break-word;
}

.cod-info-check-summary {
  display: inline-flex;
  flex-wrap: nowrap;
  align-items: center;
}

.cod-info-check-summary-sep {
  display: inline-block;
  width: 1px;
  height: 13px;
  margin: 0 7px 0 6px;
  background: #d8dbe2;
  flex-shrink: 0;
  align-self: center;
}

.cod-info-row--action .cod-info-value {
  flex: 0 0 auto;
}

.cod-info-value--mobile {
  box-sizing: border-box;
  flex: 0 0 auto;
  width: 121px;
  height: 20px;
  font-family: SFPro, SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui,
    sans-serif;
  font-weight: 400;
  font-size: 14px;
  color: #000000;
  line-height: 20px;
  text-align: left;
  font-style: normal;
  white-space: nowrap;
}

.cod-info-value--mobile-link {
  text-decoration-line: underline;
  padding: 0;
  margin: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  font: inherit;
  color: inherit;
}

.cod-info-value--muted {
  color: #999999;
}

.cod-info-inspector {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
  min-width: 0;
}

.cod-info-row:has(.cod-info-inspector) .cod-info-inspector {
  flex: 1 1 auto;
  max-width: 100%;
  overflow: hidden;
}

.cod-info-inspector-name {
  font-family: SFPro, SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui,
    sans-serif;
  font-weight: 400;
  font-size: 14px;
  color: #000000;
  line-height: 20px;
  text-align: left;
  white-space: nowrap;
  flex-shrink: 0;
}

.cod-info-inspector-ref {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: 1 1 auto;
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
  font-family: SFPro, SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui,
    sans-serif;
  font-weight: 400;
  font-size: 14px;
  color: #000000;
  line-height: 20px;
}

.cod-info-inspector-ref-text {
  flex: 1 1 auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}

:deep(.cod-info-inspector-ref-tip-host.assign-name-tip-host) {
  display: inline-flex;
  flex: 1 1 auto;
  width: auto;
  max-width: 100%;
  min-width: 0;
}

.cod-info-inspector-ref-ic {
  width: 12px;
  height: 12px;
  display: block;
  flex-shrink: 0;
  object-fit: contain;
}

.cod-info-value--contact {
  box-sizing: border-box;
  flex: 0 0 auto;
  width: 355px;
  height: 20px;
  font-family: SFPro, SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui,
    sans-serif;
  font-weight: 400;
  font-size: 14px;
  color: #000000;
  line-height: 20px;
  text-align: left;
  font-style: normal;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cod-info-action {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  box-sizing: border-box;
  margin-left: auto;
  padding: 0 14px;
  border: 1px solid #000000;
  border-radius: 999px;
  background: #ffffff;
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro', system-ui, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #000000;
  white-space: nowrap;
  cursor: default;
}

.cod-info-row--notes .cod-info-notes {
  align-self: stretch;
  width: 100%;
}

.cod-info-action--refund {
  height: 28px;
  padding: 0 8px;
  gap: 6px;
  margin-right: 0;
  border-radius: 16px;
  border: 1px solid #000000;
  width: 88px;
  cursor: pointer;
}

.cod-info-action--message {
  box-sizing: border-box;
  width: 143px;
  height: 28px;
  padding: 0 8px;
  gap: 8px;
  margin-right: 0;
  border-radius: 16px;
  border: 1px solid #000000;
}

.cod-info-action--message .cod-info-action-ic {
  width: 16px;
  height: 16px;
  min-width: 16px;
  min-height: 16px;
}

.cod-info-action--message .cod-info-action-txt {
  box-sizing: border-box;
  width: 103px;
  height: 20px;
  font-family: SFPro, SFPro;
  font-weight: 400;
  font-size: 14px;
  color: #000000;
  line-height: 20px;
  text-align: left;
  font-style: normal;
  white-space: nowrap;
}

.cod-info-action--schedule {
  box-sizing: border-box;
  width: 101px;
  height: 28px;
  padding: 0 8px;
  gap: 6px;
  margin-right: 0;
  border-radius: 16px;
  border: 1px solid #000000;
  cursor: pointer;
}

.cod-info-action--schedule .cod-info-action-ic {
  width: 16px;
  height: 16px;
  min-width: 16px;
  min-height: 16px;
}

.cod-info-action--refund .cod-info-action-ic {
  width: 16px;
  height: 16px;
  min-width: 16px;
  min-height: 16px;
}

.cod-info-action--schedule .cod-info-action-txt {
  box-sizing: border-box;
  width: 61px;
  height: 20px;
  font-family: SFPro, SFPro;
  font-weight: 400;
  font-size: 14px;
  color: #000000;
  line-height: 20px;
  text-align: left;
  font-style: normal;
  white-space: nowrap;
}

.cod-info-service-time {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex: 0 0 auto;
  min-width: 0;
}

.cod-info-value--service-time {
  flex: 0 0 auto;
}

.cod-info-preferred-host {
  position: relative;
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
}

.cod-info-preferred-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin: 0;
  border: none;
  background: transparent;
  cursor: pointer;
}

.cod-info-preferred-ic {
  width: 16px;
  height: 16px;
  display: block;
  object-fit: contain;
}

.cod-info-preferred-tip {
  position: fixed;
  z-index: 13000;
  box-sizing: border-box;
  width: 169px;
  min-height: 120px;
  padding: 10px 12px 12px;
  background: #fef39c;
  border-radius: 8px;
  box-shadow: 0 8px 16px 0 rgba(0, 0, 0, 0.1);
  text-align: left;
  pointer-events: none;
  margin: 0;
  overflow-x: hidden;
  overflow-y: auto;
}

.cod-info-preferred-tip-title {
  margin: 0 0 6px;
  font-family: SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui,
    sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #484848;
  white-space: nowrap;
}

.cod-info-preferred-tip-list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.cod-info-preferred-tip-list li {
  margin: 0;
  padding: 0;
  font-family: SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui,
    sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #484848;
  white-space: normal;
  overflow-wrap: break-word;
  word-break: break-word;
}

.cod-info-action--reschedule {
  box-sizing: border-box;
  width: 115px;
  height: 28px;
  padding: 0 8px;
  gap: 6px;
  margin-right: 0;
  border-radius: 16px;
  border: 1px solid #000000;
  cursor: pointer;
}

.cod-info-action--reschedule .cod-info-action-ic {
  width: 16px;
  height: 16px;
  min-width: 16px;
  min-height: 16px;
}

.cod-info-action--reschedule .cod-info-action-txt {
  box-sizing: border-box;
  width: 75px;
  height: 20px;
  font-family: SFPro, SFPro;
  font-weight: 400;
  font-size: 14px;
  color: #000000;
  line-height: 20px;
  text-align: left;
  font-style: normal;
  white-space: nowrap;
}

.cod-info-action--refund .cod-info-action-txt {
  font-family: SFPro, SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui,
    sans-serif;
  font-weight: 400;
  font-size: 14px;
  color: #000000;
  line-height: 20px;
  text-align: left;
  font-style: normal;
  white-space: nowrap;
}

.cod-info-action-ic {
  width: 16px;
  height: 16px;
  display: block;
  object-fit: contain;
  flex-shrink: 0;
}

.cod-info-notes {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
}

.cod-info-notes-text {
  box-sizing: border-box;
  align-self: stretch;
  width: 355px;
  max-width: 100%;
  min-height: 40px;
  margin: 0;
  font-family: SFPro, SFPro;
  font-weight: 400;
  font-size: 14px;
  color: #000000;
  line-height: 20px;
  text-align: left;
  font-style: normal;
  word-break: break-word;
}

.cod-info-action--notes {
  width: 106px;
  height: 28px;
  margin-left: 0;
  margin-right: 0;
  padding: 0 8px;
  gap: 6px;
  border-radius: 16px;
  border: 1px solid #000000;
  cursor: pointer;
  flex-shrink: 0;
  align-self: flex-end;
}

.cod-info-action--notes .cod-info-action-ic {
  width: 16px;
  height: 16px;
  min-width: 16px;
  min-height: 16px;
}

.cod-info-action--notes .cod-info-action-txt {
  box-sizing: border-box;
  width: 66px;
  height: 20px;
  font-family: SFPro, SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui,
    sans-serif;
  font-weight: 400;
  font-size: 14px;
  color: #000000;
  line-height: 20px;
  text-align: left;
  font-style: normal;
  white-space: nowrap;
}

.cod-info-row--action .cod-info-action {
  cursor: default;
}

.cod-info-row--action .cod-info-action--schedule {
  cursor: pointer;
}

.cod-info-row--action .cod-info-action--reschedule {
  cursor: pointer;
}

.cod-info-row--action .cod-info-action--refund {
  cursor: pointer;
}

.cod-info-payment-tabs {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
  margin: 0 0 12px;
}

.cod-info-payment-tab {
  box-sizing: border-box;
  width: 111px;
  height: 28px;
  padding: 0 8px;
  border: 1px solid #000000;
  border-radius: 16px;
  background: #ffffff;
  font-family: SFPro, SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui,
    sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #000000;
  text-align: center;
  white-space: nowrap;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.cod-info-payment-tab--active {
  background: #000000;
  border-color: #000000;
  color: #ffffff;
}

.cod-info-row--payment-attempt {
  align-items: center;
  gap: 14px;
}

/*
 * 布局占位 = Subscription ID 列宽（冒号在右缘，后面 gap 24px 与其它行一致）
 * 文字用 flex-end 贴右，长出部分往左溢；字号不变
 */
.cod-info .cod-info-row--payment-attempt > .cod-info-label,
.cod-info .cod-info-row--payment-method > .cod-info-label {
  flex: 0 0 var(--cod-info-subscription-label-width) !important;
  width: var(--cod-info-subscription-label-width) !important;
  max-width: var(--cod-info-subscription-label-width) !important;
  min-width: 0 !important;
  display: flex !important;
  justify-content: flex-end;
  overflow: visible;
  transform: none;
}

.cod-info-payment-attempt-badge {
  box-sizing: border-box;
  min-width: 105px;
  height: 20px;
  padding: 2px 8px;
  border-radius: 8px;
  background: #ffebeb;
  font-family: SFPro, SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui,
    sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0;
  color: #e54848;
  text-align: center;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.cod-info-action--reschedule.cod-info-action--disabled,
.cod-info-action--reschedule.cod-info-action--disabled:disabled,
.cod-info-action--refund.cod-info-action--disabled,
.cod-info-action--refund.cod-info-action--disabled:disabled {
  border-color: #cccccc;
  color: #999999;
  opacity: 1;
  pointer-events: none;
  cursor: default;
}

.cod-info-action--reschedule.cod-info-action--disabled .cod-info-action-txt,
.cod-info-action--refund.cod-info-action--disabled .cod-info-action-txt {
  color: #999999;
  font-weight: 400;
}

.cod-info-action--reschedule.cod-info-action--disabled .cod-info-action-ic,
.cod-info-action--refund.cod-info-action--disabled .cod-info-action-ic {
  opacity: 0.45;
}

.cod-info-row--action .cod-info-action--disabled,
.cod-info-row--action .cod-info-action--disabled:disabled {
  cursor: default;
  pointer-events: none;
}

.cod-info-row--timeline .cod-info-timeline {
  flex: 1 1 auto;
  min-width: 0;
}

.cod-info-timeline {
  position: relative;
  padding-left: 0;
  box-sizing: border-box;
}

.cod-info-timeline-item {
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 12px;
  padding-left: 20px;
  padding-bottom: 0;
  min-height: 20px;
  box-sizing: border-box;
}

.cod-info-timeline-item:has(.cod-info-timeline-body) {
  min-height: 40px;
}

.cod-info-timeline-item:not(:last-child) {
  padding-bottom: 44px;
}

.cod-info-timeline-item:has(.cod-info-timeline-body):not(:last-child) {
  padding-bottom: 24px;
}

.cod-info-timeline-item:not(:last-child)::after {
  content: '';
  position: absolute;
  left: 3px;
  top: 14px;
  width: 2px;
  height: 56px;
  background: #999999;
  pointer-events: none;
  z-index: 0;
}

.cod-info-timeline-body {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  flex: 1 1 auto;
  min-width: 0;
  margin-left: auto;
  gap: 0;
}

.cod-info-timeline-actor {
  box-sizing: border-box;
  height: 20px;
  font-family: SFPro, SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui,
    sans-serif;
  font-weight: 400;
  font-size: 14px;
  color: #999999;
  line-height: 20px;
  text-align: right;
  font-style: normal;
  white-space: nowrap;
}

.cod-info-timeline-item:last-child {
  padding-bottom: 0;
}

.cod-info-timeline-item::before {
  content: '';
  position: absolute;
  left: 0;
  top: 6px;
  width: 8px;
  height: 8px;
  border-radius: 2px;
  background: #999999;
  z-index: 1;
}

.cod-info-timeline-at {
  box-sizing: border-box;
  flex: 0 0 auto;
  width: auto;
  min-width: 155px;
  max-width: 46%;
  height: auto;
  min-height: 20px;
  font-family: SFPro, SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui,
    sans-serif;
  font-weight: 500;
  font-size: 14px;
  color: #999999;
  line-height: 20px;
  text-align: left;
  font-style: normal;
  white-space: nowrap;
}

.cod-info-timeline-label {
  flex: 0 0 auto;
  min-width: 0;
  box-sizing: border-box;
  height: 20px;
  font-family: SFPro, SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui,
    sans-serif;
  font-weight: 400;
  font-size: 14px;
  color: #000000;
  line-height: 20px;
  text-align: right;
  font-style: normal;
  white-space: nowrap;
}

.cod-info-timeline-item > .cod-info-timeline-label {
  margin-left: auto;
}

.cod-summary {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0;
  max-width: 100%;
  box-sizing: border-box;
  padding: 0 0 16px;
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro', system-ui, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0;
  text-align: left;
}

.cod-summary-sep {
  display: inline-block;
  width: 1px;
  height: 13px;
  margin: 0 7px 0 6px;
  background: #d8dbe2;
  flex-shrink: 0;
  align-self: center;
}

.cod-summary-token {
  font-family: inherit;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0;
  text-align: left;
}

.cod-summary-token.cod-n {
  color: #4a7a2b;
}

.cod-summary-token.cod-a {
  color: #ffb407;
}

.cod-summary-token.cod-ic,
.cod-summary-token.cod-i {
  color: #e54848;
}

.cod-summary-token.cod-u,
.cod-summary-token.cod-plain {
  color: #000000;
}

.cod-summary-token.cod-p {
  color: #000000;
}

.cod-summary-token.cod-f {
  color: #6a1b9a;
}

.cod-summary-token.cod-b {
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro', system-ui, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0;
  color: #00aeef;
  text-align: left;
}

.cod-checklist {
  list-style: none;
  margin: 0;
  padding: 0;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
}

.cod-check-item {
  padding: 16px 0;
  border-top: 1px solid #e8e8e8;
  max-width: 100%;
  box-sizing: border-box;
}

.cod-check-item:first-child {
  border-top: none;
  padding-top: 0;
}

.cod-check-top {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: 8px;
}

.cod-check-title {
  margin: 0;
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro', system-ui, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0;
  color: #000000;
  text-align: left;
  flex: 0 1 auto;
}

.cod-type-tag {
  flex-shrink: 0;
  font-size: 0.72rem;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 999px;
  line-height: 1.3;
}

.cod-type-tag--routine {
  width: 61px;
  height: 20px;
  padding: 0;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #dff4c8;
  border-radius: 8px;
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro', system-ui, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  letter-spacing: 0;
  color: #4a7a2b;
  text-align: left;
}

.cod-type-tag--baseline {
  width: 65px;
  height: 20px;
  padding: 0;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #d9f0ff;
  border-radius: 8px;
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro', system-ui, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  letter-spacing: 0;
  color: #00aeef;
  text-align: left;
}

.cod-check-status {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  font-size: 0.82rem;
  font-weight: 600;
}

.cod-status-ico {
  width: 18px;
  height: 18px;
  display: block;
  flex-shrink: 0;
  object-fit: contain;
}

.cod-check-status--attention {
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro', system-ui, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0;
  color: #ffb407;
  text-align: left;
}

.cod-check-status--attention .cod-status-ico {
  width: 16px;
  height: 16px;
}

.cod-check-status--normal {
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro', system-ui, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0;
  color: #4a7a2b;
  text-align: left;
}

.cod-check-status--normal .cod-status-ico {
  width: 16px;
  height: 16px;
}

.cod-check-status--major {
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro', system-ui, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0;
  color: #e54848;
  text-align: left;
}

.cod-check-status--major .cod-status-ico {
  width: 16px;
  height: 16px;
}

.cod-check-status--major-covered {
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro', system-ui, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0;
  color: #e54848;
  text-align: left;
}

.cod-check-status--major-covered .cod-status-ico {
  width: 16px;
  height: 16px;
}

.cod-check-status--unable {
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro', system-ui, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0;
  color: #000000;
  text-align: left;
}

.cod-check-status--unable .cod-status-ico {
  width: 16px;
  height: 16px;
}

.cod-check-status--pending {
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro', system-ui, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0;
  color: #cccccc;
  text-align: left;
}

.cod-check-status--pending .cod-status-ico {
  width: 16px;
  height: 16px;
}

.cod-check-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 10px;
  font-size: 0.82rem;
  min-width: 0;
  max-width: 100%;
}

.cod-check-loc {
  min-width: 0;
  flex: 1 1 auto;
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro', system-ui, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0;
  color: #000000;
  text-align: left;
}

.cod-check-link {
  flex-shrink: 0;
  border: none;
  background: transparent;
  padding: 0;
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro', system-ui, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0;
  color: #000000;
  text-align: right;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 2px;
}

.cod-check-link:disabled {
  cursor: default;
  opacity: 0.55;
}

.cod-check-link:not(:disabled):hover {
  color: #000000;
}

.cod-check-chev {
  width: 16px;
  height: 16px;
  display: block;
  object-fit: contain;
  flex-shrink: 0;
}

.cod-check-item--selected {
  background: #f7f8fa;
}

.cod-inline-detail {
  box-sizing: border-box;
  min-width: 0;
  padding: var(--cod-page-pad);
}
</style>
